import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useCookieStoreContext } from '../store/useCookieStore';
import { Button } from './icons/Button';
import { Close } from './icons/Close';

interface CustomConsentCookieModalProps {
  closeCustomConsentModal: () => void;
  toggleFacebook: () => void;
  toggleGoogleAds: () => void;
  toggleGoogleAnalytics: () => void;
  toggleMicrosoftAds: () => void;
  handleAcceptAllCookies: () => void;
  facebook: boolean;
  googleAds: boolean;
  googleAnalytics: boolean;
  microsoftAds: boolean;
}

const messages = defineMessages({
  decline: {
    id: 'KVaAkH',
    defaultMessage: 'Tout refuser',
  },
  accept: {
    id: 'RNf0XJ',
    defaultMessage: 'Tout accepter',
  },
  title: {
    id: 'SuWG99',
    defaultMessage: 'Paramètres des cookies',
  },
  first_description: {
    id: 'PRx0dm',
    defaultMessage:
      `Nous utilisons des cookies, certains sont strictement nécessaires, et d'autres sont optionnels. Les cookies sont utilisés pour la personnalisation des annonces. Pour plus d'informations, lire notre <a href="/politique_de_confidentialite" target="_blank" class="text-primaryNewComparateurFibre underline">Politique de protection des données</a> et les <a href="/gestion_des_cookies/" target="_blank" class="text-primaryNewComparateurFibre underline">Cookies</a>. Vous pouvez en apprendre plus à propos de Google Ads et de ses traitements de données dans sa <a href="https://business.safety.google/privacy/" target="_blank" class="text-primaryNewComparateurFibre underline">politique de confidentialité</a>.`,
  },
  second_description: {
    id: 'PRx0dn',
    defaultMessage:
      `Vous pouvez personnaliser vos paramètres en activant les types de cookies avec lesquels vous êtes d'accord.`,
  },
  strictly_necessary: {
    id: 'PRx0dn',
    defaultMessage:
      `Facebook Pixel`,
  },
  strictly_necessary_description: {
    id: 'PRx0dl',
    defaultMessage:
      `<a href="https://transparency.meta.com/fr-fr/" target="_blank">ⓘ Identifie les visiteurs de Facebook</a>`,
  },
  google_ads: {
    id: 'PRx0dm',
    defaultMessage:
      `Google Ads`,
  },
  google_ads_description: {
    id: 'PRx0dy',
    defaultMessage:
      `<a href="https://business.safety.google/privacy/" target="_blank">ⓘ Mesure les performances des campagnes Google</a>`,
  },
  google_analytics: {
    id: 'PRx0dz',
    defaultMessage:
      `Google Analytics`,
  },
  google_analytics_description: {
    id: 'PRx0da',
    defaultMessage:
      `<a href="https://support.google.com/analytics/answer/6004245?hl=fr" target="_blank">ⓘ Mesure de l'audience</a>`,
  },
  microsoft_ads: {
    id: 'PRx0dz',
    defaultMessage:
      `Microsoft Ads`,
  },
  microsoft_ads_description: {
    id: 'PRx0da',
    defaultMessage:
      `<a href="https://help.ads.microsoft.com/#apex/ads/en/60226/23" target="_blank">ⓘ Mesure les performances des campagnes Microsoft</a>`,
  },
  save_setting: {
    id: 'PRx0d8',
    defaultMessage:
      `Sauvegarder paramètres.`,
  },
});

export const CustomConsentCookieModal = React.forwardRef<HTMLDivElement, CustomConsentCookieModalProps>(
  ({ closeCustomConsentModal, facebook, googleAds, googleAnalytics, microsoftAds, toggleFacebook, toggleGoogleAds, toggleGoogleAnalytics, toggleMicrosoftAds, handleAcceptAllCookies }, ref) => {
    const { formatMessage } = useIntl();
    const { localStorageVariable, handleDeclineCookies, handleAcceptCookiesAll } =
      useCookieStoreContext();

    const handleDecline = () => {
      handleDeclineCookies();
      closeCustomConsentModal();
    };

    return localStorageVariable === null ? (
      <div>
        <div className="fixed inset-0 z-50 bg-black opacity-50"></div>
        <div
          ref={ref}
          className="fixed inset-0 z-50 md:flex md:justify-center md:items-center w-screen"
        >
          <div className="bg-white h-full w-full px-10 py-12 text-sm lg:text-[15px] md:h-auto md:w-[98%] lg:w-1/2 xl:w-[38%]">
            <section className='flex flex-col gap-y-4'>
              <div className='flex flex-col gap-y-2'>
                <div className='flex justify-between'>
                  <h2 className='font-bold text-[15px]' dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.title)}` }}></h2>
                  <Close className='-mt-4 rounded bg-primaryNewComparateurFibre hover:bg-primary cursor-pointer' onClick={closeCustomConsentModal} />
                </div>
                <div className='text-[13px] flex flex-col gap-y-2'>
                  <p dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.first_description)}` }}></p>
                  <p dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.second_description)}` }}></p>
                </div>
              </div>

              <div className="flex gap-x-3 border-primaryNewComparateurFibre justify-center items-center border-b-[1px] pb-3">
                <Button className="py-2.5 px-8 lg:px-4 normal-case bg-primaryNewComparateurFibre hover:border-primaryNewComparateurFibre hover:bg-white hover:text-primaryNewComparateurFibre hover:duration-300 hover:ease-linear hover:border-2" onClick={handleDecline}>
                  {formatMessage(messages.decline)}
                </Button>
                <Button className="py-2.5 px-8 lg:px-4 normal-case bg-primaryNewComparateurFibre hover:border-primaryNewComparateurFibre hover:bg-white hover:text-primaryNewComparateurFibre hover:duration-300 hover:ease-linear hover:border-2" onClick={handleAcceptCookiesAll}>
                  {formatMessage(messages.accept)}
                </Button>
              </div>
            </section>

            <section className='flex flex-col gap-y-4 px-4 py-6'>
              <article className='flex flex-col gap-y-1'>
                <div className='flex justify-between items-center'>
                  <h3 className='font-bold text-[15px]' dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.strictly_necessary)}` }}></h3>

                  <span onClick={toggleFacebook} className={`border-gray-300 rounded-3xl border-[3px] cursor-pointer w-14 flex items-center h-8 p-0.5 ${facebook ? 'bg-primaryNewComparateurFibre justify-end' : 'bg-gray-500 justify-start'}`}>
                    <span className='w-6 h-6 rounded-full bg-white'></span>
                  </span>
                </div>
                <p className='w-3/4 lg:w-full' dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.strictly_necessary_description)}` }}></p>
              </article>

              <article className='flex flex-col gap-y-1'>
                <div className='flex justify-between items-center'>
                  <h3 className='font-bold text-[15px]' dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.google_ads)}` }}></h3>

                  <span onClick={toggleGoogleAds} className={`border-gray-300 rounded-3xl border-[3px] cursor-pointer w-14 flex items-center h-8 p-0.5 ${googleAds ? 'bg-primaryNewComparateurFibre justify-end' : 'bg-gray-500 justify-start'}`}>
                    <span className='w-6 h-6 rounded-full bg-white'></span>
                  </span>
                </div>
                <p className='w-3/4 lg:w-full' dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.google_ads_description)}` }}></p>
              </article>

              <article className='flex flex-col gap-y-1'>
                <div className='flex justify-between items-center'>
                  <h3 className='font-bold text-[15px]' dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.google_analytics)}` }}></h3>

                  <span onClick={toggleGoogleAnalytics} className={`border-gray-300 rounded-3xl border-[3px] cursor-pointer w-14 flex items-center h-8 p-0.5 ${googleAnalytics ? 'bg-primaryNewComparateurFibre justify-end' : 'bg-gray-500 justify-start'}`}>
                    <span className='w-6 h-6 rounded-full bg-white'></span>
                  </span>
                </div>
                <p className='w-3/4 lg:w-full' dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.google_analytics_description)}` }}></p>
              </article>

              <article className='flex flex-col gap-y-1'>
                <div className='flex justify-between items-center'>
                  <h3 className='font-bold text-[15px]' dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.microsoft_ads)}` }}></h3>

                  <span onClick={toggleMicrosoftAds} className={`border-gray-300 rounded-3xl border-[3px] cursor-pointer w-14 flex items-center h-8 p-0.5 ${microsoftAds ? 'bg-primaryNewComparateurFibre justify-end' : 'bg-gray-500 justify-start'}`}>
                    <span className='w-6 h-6 rounded-full bg-white'></span>
                  </span>
                </div>
                <p className='w-3/4 lg:w-full' dangerouslySetInnerHTML={{ __html: `${formatMessage(messages.microsoft_ads_description)}` }}></p>
              </article>

              <button className="mt-4 bg-white text-primaryNewComparateurFibre hover:text-white border-primaryNewComparateurFibre border-[3px] p-3 lg:text-[16px] hover:bg-primaryNewComparateurFibre hover:duration-300 hover:ease-linear rounded" onClick={handleAcceptAllCookies}>{formatMessage(messages.save_setting)}</button>
            </section>
          </div>
        </div>
      </div>
    ) : null;
  },
);
